import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { spPromoRequisitionMembersQueryOptions } from '../api/productRequisition.api';
import { getSpPromoMemberColumns } from './RequisitionColumns';
import type {
  SpPromoRequisitionRow,
  SpPromoRequisitionSearch,
} from '../types/productRequisition.types';

interface SpPromoMembersModalProps {
  specimen: SpPromoRequisitionRow | null;
  params: SpPromoRequisitionSearch;
  onClose: () => void;
}

export function SpPromoMembersModal({ specimen, params, onClose }: SpPromoMembersModalProps) {
  const specimenId = specimen?.specimenId ?? 0;
  const { data, isLoading, error, refetch } = useQuery({
    ...spPromoRequisitionMembersQueryOptions(specimenId, params),
    enabled: specimen !== null,
  });

  const columns = useMemo(() => getSpPromoMemberColumns(), []);

  const stats = [
    { key: 'required', label: 'Requisition Qty', value: data?.totals.required ?? 0 },
    { key: 'approved', label: 'Reviewed Qty', value: data?.totals.approved ?? 0 },
    { key: 'teacher', label: 'Teacher Qty', value: data?.totals.teacher ?? 0 },
    { key: 'targetSale', label: 'Target Sale Qty', value: data?.totals.targetSale ?? 0 },
  ];

  return (
    <Dialog
      open={specimen !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{specimen?.specimenName ?? 'Specimen'}</DialogTitle>
        </DialogHeader>

        {error ? (
          <ErrorState
            error={error}
            fallbackTitle="Could not load who asked for this specimen"
            onRetry={() => void refetch()}
            size="compact"
          />
        ) : (
          <div className="flex flex-col gap-4">
            <StatCardGrid stats={stats} isLoading={isLoading} />
            <DataTable
              data={data?.items ?? []}
              columns={columns}
              isLoading={isLoading}
              stickyColumnId="memberName"
              emptyMessage="Nobody asked for this specimen in the period"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
